import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { PlaidLinkOnSuccessMetadata, usePlaidLink } from "react-plaid-link";
import { toast } from "@outpave/ds";
import { START_BANK_LINK } from "graphql/company";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import AccountType from "enums/AccountType";
import useCompleteBankLink, {
  CompletedBankLink,
} from "hooks/useCompleteBankLink";
import BankTermsDialog from "./BankTermsDialog";

interface PlaidBankLinkProps {
  /** The card program whose issuer the banks are linked under. */
  programId: string;
  accountType?: AccountType;
  /** Called with the saved banks, also when a later Retry saves them. */
  onLinked: (bankAccountIds: string[]) => void;
  onDone: () => void;
}

/**
 * Takes the ACH authorization, verifies the banks with Plaid, and links every
 * account Plaid reports under one program's issuer. Mounted only for as long
 * as the flow runs.
 */
const PlaidBankLink: React.FC<PlaidBankLinkProps> = ({
  programId,
  accountType = AccountType.BANK,
  onLinked,
  onDone,
}) => {
  const { t } = useTranslation();
  const [termsOpen, setTermsOpen] = useState(true);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const authorizedAt = useRef<number | null>(null);

  const [startLink, { loading: starting }] = useMutation(START_BANK_LINK, {
    onCompleted: (data) => {
      const token = data?.startBankLink?.linkToken;
      if (token) {
        setLinkToken(token);
      } else {
        toast.error(t("BankLinkFailed"));
        onDone();
      }
    },
    onError: (error) => {
      toast.error(error.message || t("BankLinkFailed"));
      onDone();
    },
  });

  const { completeBankLink, completing } = useCompleteBankLink();

  const reportLink = (link: CompletedBankLink) => {
    const ids = link.accounts.map((account) => account.id);
    if (ids.length) {
      toast.success(t("BankLinkedToast"));
      onLinked(ids);
    }
    link.skipped.forEach((account) => {
      toast.warning(
        t("BankNotLinked", {
          name: account.name ?? account.last4 ?? "",
          reason: account.reason ?? "",
        }),
      );
    });
  };

  const handleTermsClosed = (accepted: boolean) => {
    setTermsOpen(false);
    if (!accepted) {
      onDone();
      return;
    }
    authorizedAt.current = Date.now();
    startLink({ variables: { programId, type: accountType } });
  };

  // Plaid reports every account at the institution and the person cannot
  // deselect one, so all of them are sent.
  const handleSuccess = async (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    setLinkToken(null);
    const accountIds = metadata.accounts.map((account) => account.id);
    if (!accountIds.length || !authorizedAt.current) {
      toast.error(t("BankLinkFailed"));
      onDone();
      return;
    }
    const link = await completeBankLink(
      {
        programId,
        type: accountType,
        publicToken,
        accountIds,
        authorizedAt: authorizedAt.current,
        ...(metadata.institution?.name && {
          institutionName: metadata.institution.name,
        }),
      },
      reportLink,
    );
    if (link) reportLink(link);
    onDone();
  };

  return (
    <>
      <BankTermsDialog open={termsOpen} onClose={handleTermsClosed} />
      {linkToken && (
        <PlaidLauncher
          linkToken={linkToken}
          onSuccess={handleSuccess}
          onExit={() => {
            setLinkToken(null);
            onDone();
          }}
        />
      )}
      <LoadingWithoutModal open={starting || completing} />
    </>
  );
};

interface PlaidLauncherProps {
  linkToken: string;
  onSuccess: (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => void;
  onExit: () => void;
}

const PlaidLauncher: React.FC<PlaidLauncherProps> = ({
  linkToken,
  onSuccess,
  onExit,
}) => {
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => onExit(),
  });

  useEffect(() => {
    if (ready) open();
  }, [ready, open]);

  return null;
};

export default PlaidBankLink;
